import { gql, useQuery } from '@apollo/client';

import { useTeam } from '../../context/TeamProvider';
import type {
  NexoyaContentFilterInput,
  NexoyaContentLabelAssignment,
  NexoyaContentLabelCategory,
  NexoyaContentLabelCategoryValue,
  NexoyaContentV2,
  NexoyaQueryFilterContentsArgs,
} from '../../types';

import { CONTENT_LABEL_VALUE_REF_FRAGMENT, LABELABLE_CONTENT_FRAGMENT } from './fragments';

/**
 * Content Labels v2 — the label sheet's live preview: which contents of one
 * portfolio a DRAFT rule reaches, with each content's current label state so the
 * sheet can warn about other labels' rules and hand-set labels.
 *
 * `filterContents` is the same filter engine the label rules run on server-side.
 * `excludePortfolioContents: false` keeps the portfolio's own contents in and is
 * fixed here. `inPortfolioOnly` is NOT: it follows whether a `portfolioId` was
 * given, so the request says what it narrows by instead of naming a portfolio
 * scope it has no portfolio for. With neither, the server filters team-wide —
 * the "All team contents" scope's preview.
 *
 * Unpaginated, and two server behaviours mean it must not always be sent:
 * - `filters: []` answers with EVERY content — this hook never sends it.
 * - on an EMPTY portfolio `inPortfolioOnly` stops restricting and answers with
 *   team-wide matches — the caller (`useLabelRulePreview`) skips when the scope's
 *   total is 0.
 */
export const CONTENT_LABEL_RULE_PREVIEW_QUERY = gql`
  query ContentLabelRulePreview(
    $teamId: Float!
    $portfolioId: Float
    $filters: [ContentFilterInput!]!
    $inPortfolioOnly: Boolean!
  ) {
    filterContents(
      teamId: $teamId
      portfolioId: $portfolioId
      filters: $filters
      excludePortfolioContents: false
      inPortfolioOnly: $inPortfolioOnly
    ) {
      ...LabelableContentFields
      labelAssignments {
        labelStatus
        contentLabelCategory {
          contentLabelCategoryId
        }
        matches {
          ...ContentLabelValueRef
        }
        assignment {
          ...ContentLabelValueRef
        }
      }
    }
  }
  ${LABELABLE_CONTENT_FRAGMENT}
  ${CONTENT_LABEL_VALUE_REF_FRAGMENT}
`;

/** The entity key alone — see `CONTENT_LABEL_VALUE_REF_FRAGMENT` for why nothing else. */
type ValueRef = Pick<NexoyaContentLabelCategoryValue, 'contentLabelCategoryValueId'>;

export type RulePreviewContent = Pick<NexoyaContentV2, 'contentId' | 'title' | 'objective' | 'parent' | 'provider'> & {
  biddingStrategy?: NexoyaContentV2['biddingStrategy'];
  labelAssignments: Array<
    Pick<NexoyaContentLabelAssignment, 'labelStatus'> & {
      contentLabelCategory: Pick<NexoyaContentLabelCategory, 'contentLabelCategoryId'>;
      matches: ValueRef[];
      assignment?: ValueRef | null;
    }
  >;
};

export type ContentLabelRulePreviewResponse = { filterContents: RulePreviewContent[] };

export type ContentLabelRulePreviewVariables = Pick<
  NexoyaQueryFilterContentsArgs,
  'teamId' | 'portfolioId' | 'filters'
> & { inPortfolioOnly: boolean };

/**
 * `fetchPolicy: 'network-only'`: the rows carry OTHER labels' saved-rule matches
 * and the manual state, which any label mutation can change, and none of the
 * mutations' cache effects reach `filterContents` — a cached answer for the same
 * draft would show stale competitors after the sheet is reopened.
 */
export function useContentLabelRulePreviewQuery({
  portfolioId,
  filters,
  skip,
}: {
  portfolioId: number | null;
  filters: NexoyaContentFilterInput[];
  skip: boolean;
}) {
  const { teamId } = useTeam();

  return useQuery<ContentLabelRulePreviewResponse, ContentLabelRulePreviewVariables>(CONTENT_LABEL_RULE_PREVIEW_QUERY, {
    variables: { teamId, portfolioId, filters, inPortfolioOnly: portfolioId != null },
    skip: skip || !teamId || !filters?.length,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
}
